import React, { useEffect, createContext, useContext, ReactNode, useState } from 'react';
import type { ReactPluginType as ReactPlugin } from '@webcore/services/telemetry/applicationInsightsBundle';
import { UserContext } from "@webcore/models/coreModels.ts";
import { ITelemetryService, NoOpTelemetryService, telemetryServiceFactory } from '@webcore/services/telemetry/TelemetryService';
import { ConversionConfig } from '@webcore/services/telemetry/googleAnalytics';
import Log from "@webcore/services/log/log.ts";

interface TelemetryContextType {
  setTelemetryAuthenticatedUserContext: (userContext: UserContext | null) => void;
  trackEvent: (name: string, properties?: Record<string, any>, measurements?: Record<string, number>) => void;
  trackPageView: (name?: string, uri?: string, properties?: Record<string, any>) => void;
  trackException: (exception: Error, properties?: Record<string, any>) => void;
  trackMetric: (name: string, average: number, properties?: Record<string, any>) => void;
  trackConversion: (eventName: string, config?: ConversionConfig) => Promise<void>;
  flushTelemetry: () => void;
}

const TelemetryContext = createContext<TelemetryContextType | undefined>(undefined);

export const useTelemetry = (): TelemetryContextType => {
  const context = useContext(TelemetryContext);
  if (context === undefined) {
    throw new Error('useTelemetry must be used within a TelemetryProvider');
  }
  return context;
};

interface TelemetryProviderProps {
  children: ReactNode;
}

export const TelemetryProvider: React.FC<TelemetryProviderProps> = ({ children }) => {
  const [telemetryService, setTelemetryService] = useState<ITelemetryService>(() => new NoOpTelemetryService());
  const [reactPluginContext, setReactPluginContext] = useState<{
    AppInsightsContext: React.Context<ReactPlugin>;
    reactPlugin: ReactPlugin
  } | null>(null);

  useEffect(() => {
    // Asynchronously initialize the telemetry service
    // All implementation details (checking config, importing libraries, etc.) are handled by the service
    (async () => {
      try {
        const service = await telemetryServiceFactory();
        setTelemetryService(service);

        // Get the React plugin context if available
        const pluginContext = service.getReactPluginContext();
        if (pluginContext) {
          setReactPluginContext(pluginContext);
        }
      } catch (error) {
        console.error('Failed to initialize telemetry:', error);
        // Keep the no-op service on error
      }
    })();
  }, []);

  // Create stable context value that delegates to the current telemetry service
  const contextValue: TelemetryContextType = {
    setTelemetryAuthenticatedUserContext: (userContext: UserContext | null) =>
      telemetryService.setTelemetryAuthenticatedUserContext(userContext),
    trackEvent: (name: string, properties?: Record<string, any>, measurements?: Record<string, number>) =>
      telemetryService.trackEvent(name, properties, measurements),
    trackPageView: (name?: string, uri?: string, properties?: Record<string, any>) =>
      telemetryService.trackPageView(name, uri, properties),
    trackException: (exception: Error, properties?: Record<string, any>) =>
      telemetryService.trackException(exception, properties),
    trackMetric: (name: string, average: number, properties?: Record<string, any>) =>
      telemetryService.trackMetric(name, average, properties),
    trackConversion: (eventName: string, config?: ConversionConfig) =>
      telemetryService.trackConversion(eventName, config),
    flushTelemetry: () => telemetryService.flushTelemetry()
  };

  // If React plugin context is not available, render without it
  if (reactPluginContext === null) {
    return (
      <TelemetryContext.Provider value={contextValue}>
        {children}
      </TelemetryContext.Provider>
    );
  }

  // Once Application Insights React plugin is loaded, wrap with AppInsightsContext
  const { AppInsightsContext, reactPlugin } = reactPluginContext;
  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <TelemetryContext.Provider value={contextValue}>
        {children}
      </TelemetryContext.Provider>
    </AppInsightsContext.Provider>
  );
};

export default TelemetryProvider;
